import React, { useDeferredValue, useEffect, useId, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSiteContent } from '../../context/SiteContentContext';
import { buildSearchIndex, getAlgoliaSearchConfig, searchAlgolia, searchSite } from '../../lib/siteSearch';

function ShortcutLinks({ shortcuts, onNavigate }) {
  if (!shortcuts?.length) {
    return null;
  }

  return (
    <div className='search-shortcuts'>
      {shortcuts.map((shortcut) => (
        <Link key={shortcut.href} className='search-shortcut' to={shortcut.href} onClick={onNavigate}>
          {shortcut.label}
        </Link>
      ))}
    </div>
  );
}

function SearchResultLink({ result, onNavigate }) {
  const content = (
    <>
      <span className='search-result-type'>{result.type}</span>
      <span className='search-result-title'>{result.title}</span>
      <span className='search-result-description'>{result.description}</span>
    </>
  );

  if (/^https?:\/\//i.test(result.href)) {
    return (
      <a className='search-result' href={result.href} onClick={onNavigate} rel='noreferrer' target='_blank'>
        {content}
      </a>
    );
  }

  return (
    <Link className='search-result' to={result.href} onClick={onNavigate}>
      {content}
    </Link>
  );
}

export function SearchPanel({ isOpen, onClose }) {
  const { siteContent } = useSiteContent();
  const inputId = useId();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searchStatus, setSearchStatus] = useState({ loading: false, source: 'idle' });
  const deferredQuery = useDeferredValue(query);
  const searchIndex = useMemo(() => buildSearchIndex(siteContent), [siteContent]);
  const algoliaConfig = useMemo(
    () => getAlgoliaSearchConfig(siteContent.siteSettings.search, siteContent.siteSettings),
    [siteContent]
  );
  const shortcuts = siteContent.siteSettings.search?.shortcuts || [];
  const placeholder = siteContent.siteSettings.search?.placeholder || 'Search pages, proof, and answers';

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setResults([]);
      setSearchStatus({ loading: false, source: 'idle' });
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    let isCancelled = false;
    const normalizedQuery = deferredQuery.trim();

    if (!isOpen || !normalizedQuery) {
      setResults([]);
      setSearchStatus({ loading: false, source: 'idle' });
      return undefined;
    }

    setResults([]);
    setSearchStatus({ loading: Boolean(algoliaConfig), source: algoliaConfig ? 'algolia' : 'local' });

    const runSearch = async () => {
      if (algoliaConfig) {
        try {
          const algoliaResults = await searchAlgolia(algoliaConfig, normalizedQuery);

          if (isCancelled) {
            return;
          }

          setResults(algoliaResults);
          setSearchStatus({ loading: false, source: 'algolia' });
          return;
        } catch (error) {
          console.error('Falling back to bundled search because Algolia could not be reached.', error);
        }
      }

      if (isCancelled) {
        return;
      }

      setResults(searchSite(searchIndex, normalizedQuery));
      setSearchStatus({ loading: false, source: 'local' });
    };

    runSearch();

    return () => {
      isCancelled = true;
    };
  }, [algoliaConfig, deferredQuery, isOpen, searchIndex]);

  if (!isOpen) {
    return null;
  }

  const hasQuery = Boolean(query.trim());
  const isLoading = searchStatus.loading;
  const isUsingLocalFallback = hasQuery && Boolean(algoliaConfig) && searchStatus.source === 'local';

  return (
    <div className='search-panel' role='dialog' aria-label='Site search'>
      <div className='search-input-shell'>
        <label htmlFor={inputId} className='visually-hidden'>
          Search Rivelin Robotics
        </label>
        <span className='search-input-icon' aria-hidden='true'>
          <svg viewBox='0 0 24 24' focusable='false'>
            <circle
              cx='11'
              cy='11'
              r='5.5'
              fill='none'
              stroke='currentColor'
              strokeWidth='1.8'
            />
            <path d='M15.2 15.2 19.5 19.5' fill='none' stroke='currentColor' strokeLinecap='round' strokeWidth='1.8' />
          </svg>
        </span>
        <input
          id={inputId}
          className='search-input'
          type='search'
          placeholder={placeholder}
          value={query}
          autoFocus
          onChange={(event) => setQuery(event.target.value)}
        />
        <button className='search-close' type='button' onClick={onClose}>
          Close
        </button>
      </div>
      {!hasQuery ? (
        <div className='search-empty-state'>
          <p className='search-empty-copy'>Search the site or jump straight to a key route.</p>
          <ShortcutLinks shortcuts={shortcuts} onNavigate={onClose} />
        </div>
      ) : (
        <>
          {isUsingLocalFallback ? (
            <p className='search-empty-copy'>Showing local fallback results while Algolia is unavailable.</p>
          ) : null}
          <div className='search-results' aria-busy={isLoading}>
            {isLoading ? <p className='search-empty'>Searching Algolia...</p> : null}
            {!isLoading && results.length === 0 ? <p className='search-empty'>No matches yet. Try another keyword.</p> : null}
            {results.map((result) => (
              <SearchResultLink key={result.objectID || result.href} result={result} onNavigate={onClose} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
